import { inject, Injectable } from '@angular/core';
import { type ThemePalette } from '@angular/material/core';
import { BasicDialogService } from '@principle-theorem/ng-shared';
import {
  MarketingCampaignEntry,
  MarketingCampaignEntryLog,
  stafferToNamedDoc,
} from '@principle-theorem/principle-core';
import {
  CampaignEntryPauseReason,
  IMarketingCampaignEntry,
  IStaffer,
  MarketingCampaignEntryLogEvent,
  MarketingCampaignEntryStatus,
  MarketingCampaignStepConversionStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  Firestore,
  INamedDocument,
  snapshot,
  WithRef,
} from '@principle-theorem/shared';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { OrganisationService } from '../../organisation.service';

export interface IEntryAction {
  label: string;
  icon: string;
  iconColorClass?: string;
  buttonColor?: ThemePalette;
  tooltip: string;
  run: () => void | Promise<void>;
}

export interface IEntryActions {
  primary?: IEntryAction;
  secondary: IEntryAction[];
}

@Injectable()
export class CampaignEntryManagementService {
  private _dialog = inject(BasicDialogService);
  private _organisation = inject(OrganisationService);
  convertTooltip = 'Mark this entry as converted';
  unenrolTooltip = 'Remove this entry from the campaign';
  unsubscribeTooltip = 'Patient has opted out of marketing';
  pauseTooltip = 'Pause this entry';
  resumeTooltip = 'Resume this paused entry';

  getActions$(
    entry: WithRef<IMarketingCampaignEntry>
  ): Observable<IEntryActions> {
    return combineLatest([
      this.canResume$(entry),
      this.canConvert$(entry),
      this.canPause$(entry),
      this.canUnenrol$(entry),
      this.canUnsubscribe$(entry),
    ]).pipe(
      map(([canResume, canConvert, canPause, canUnenrol, canUnsubscribe]) => {
        const resumeLabel = this._getResumeLabel(entry);
        const allActions: (IEntryAction & {
          available: boolean;
          priority: number;
        })[] = [
          {
            available: canResume,
            priority: 0,
            label: resumeLabel,
            icon: 'play_arrow',
            buttonColor: 'primary',
            iconColorClass: '!text-primary-500',
            tooltip: this.resumeTooltip,
            run: () => this.resume(entry),
          },
          {
            available: canConvert,
            priority: 1,
            label: 'Convert Entry',
            icon: 'check_circle',
            buttonColor: 'primary',
            iconColorClass: '!text-primary-500',
            tooltip: this.convertTooltip,
            run: () => this.markAsConverted(entry),
          },
          {
            available: canPause,
            priority: 2,
            label: 'Pause Entry',
            icon: 'pause',
            iconColorClass: '!text-amber-500',
            tooltip: this.pauseTooltip,
            run: () => this.pause(entry),
          },
          {
            available: canUnenrol,
            priority: 3,
            label: 'Unenrol Entry',
            icon: 'outbound',
            iconColorClass: '!text-amber-500',
            tooltip: this.unenrolTooltip,
            run: () => this.unenrol(entry),
          },
          {
            available: canUnsubscribe,
            priority: 4,
            label: 'Unsubscribe Entry',
            icon: 'outbound',
            iconColorClass: '!text-warn-500',
            tooltip: this.unsubscribeTooltip,
            run: () => this.unsubscribe(entry),
          },
        ];

        const available = allActions
          .filter((action) => action.available)
          .sort((actionA, actionB) => actionA.priority - actionB.priority);

        const [primary, ...secondary] = available;
        return {
          primary,
          secondary,
        };
      })
    );
  }

  canConvert$(entry: WithRef<IMarketingCampaignEntry>): Observable<boolean> {
    if (!entry.currentStep) {
      return of(false);
    }
    if (MarketingCampaignEntry.isPaused(entry)) {
      return of(false);
    }
    return Firestore.doc$(entry.currentStep).pipe(
      map(
        (step) =>
          step?.status !== MarketingCampaignStepConversionStatus.Converted
      )
    );
  }

  canUnenrol$(entry: WithRef<IMarketingCampaignEntry>): Observable<boolean> {
    const canUnenrol = ![
      MarketingCampaignEntryStatus.Completed,
      MarketingCampaignEntryStatus.Unenrolled,
    ].includes(entry.status);
    return of(canUnenrol);
  }

  canUnsubscribe$(
    entry: WithRef<IMarketingCampaignEntry>
  ): Observable<boolean> {
    const canUnsubscribe =
      entry.status !== MarketingCampaignEntryStatus.Unsubscribed;
    return of(canUnsubscribe);
  }

  async markAsConverted(
    entry: WithRef<IMarketingCampaignEntry>
  ): Promise<void> {
    const confirmed = await this._dialog.confirm({
      title: 'Convert Entry',
      prompt: `This will mark the entry as converted. Are you sure?`,
      submitLabel: 'Convert',
      submitColor: 'primary',
    });
    if (!confirmed) {
      return;
    }
    await MarketingCampaignEntry.convertAtCurrentStep(entry);
    const performedBy = await this._getPerformedBy();
    await MarketingCampaignEntryLog.createEntryLog(
      entry,
      MarketingCampaignEntryLogEvent.StatusChange,
      {
        message: 'Entry converted',
        performedBy,
      }
    );
  }

  async unenrol(entry: WithRef<IMarketingCampaignEntry>): Promise<void> {
    const confirmed = await this._dialog.confirm({
      title: 'Unenrol Entry',
      prompt: `Are you sure you want to unenrol this entry from the campaign?`,
      submitLabel: 'Unenrol',
      submitColor: 'warn',
    });
    if (!confirmed) {
      return;
    }
    await MarketingCampaignEntry.unenrol(entry);
    const performedBy = await this._getPerformedBy();
    await MarketingCampaignEntryLog.createEntryLog(
      entry,
      MarketingCampaignEntryLogEvent.StatusChange,
      {
        message: 'Entry un-enrolled',
        performedBy,
      }
    );
  }

  async unsubscribe(entry: WithRef<IMarketingCampaignEntry>): Promise<void> {
    const confirmed = await this._dialog.confirm({
      title: 'Unsubscribe Patient',
      prompt: `Are you sure you want to unsubscribe this patient from the campaign?`,
      submitLabel: 'Unsubscribe',
      submitColor: 'warn',
    });
    if (!confirmed) {
      return;
    }
    await MarketingCampaignEntry.unsubscribe(entry);
    const performedBy = await this._getPerformedBy();
    await MarketingCampaignEntryLog.createEntryLog(
      entry,
      MarketingCampaignEntryLogEvent.StatusChange,
      {
        message: 'Patient unsubscribed',
        performedBy,
      }
    );
  }

  canPause$(entry: WithRef<IMarketingCampaignEntry>): Observable<boolean> {
    const canPause = entry.status === MarketingCampaignEntryStatus.Active;
    return of(canPause);
  }

  canResume$(entry: WithRef<IMarketingCampaignEntry>): Observable<boolean> {
    const canResume =
      MarketingCampaignEntry.isPaused(entry) ||
      entry.status === MarketingCampaignEntryStatus.Unenrolled ||
      entry.status === MarketingCampaignEntryStatus.Converted;
    return of(canResume);
  }

  async pause(entry: WithRef<IMarketingCampaignEntry>): Promise<void> {
    const confirmed = await this._dialog.confirm({
      title: 'Pause Entry',
      prompt: 'Are you sure you want to pause this entry?',
      submitLabel: 'Pause',
      submitColor: 'warn',
    });
    if (!confirmed) {
      return;
    }
    await MarketingCampaignEntry.pause(
      entry,
      CampaignEntryPauseReason.UserInitiated
    );
    const performedBy = await this._getPerformedBy();
    await MarketingCampaignEntryLog.createEntryLog(
      entry,
      MarketingCampaignEntryLogEvent.StatusChange,
      {
        message: 'Entry paused',
        performedBy,
      }
    );
  }

  async resume(entry: WithRef<IMarketingCampaignEntry>): Promise<void> {
    const isUnenrolled =
      entry.status === MarketingCampaignEntryStatus.Unenrolled;
    const isConverted = entry.status === MarketingCampaignEntryStatus.Converted;
    const isRestart = isUnenrolled || isConverted;
    const title = isRestart ? 'Re-enrol Entry' : 'Resume Entry';
    const prompt = [
      isRestart
        ? 'Are you sure you want to re-enrol this entry into the campaign?'
        : 'Are you sure you want to resume this entry?',
      'If the next step was scheduled to run while this entry was paused, it will be moved to the next available date.',
    ];
    const submitLabel = isRestart ? 'Re-enrol' : 'Resume';

    const confirmed = await this._dialog.confirm({
      title,
      prompt,
      submitLabel,
      submitColor: 'primary',
    });
    if (!confirmed) {
      return;
    }

    if (isUnenrolled) {
      await MarketingCampaignEntry.reenrol(entry);
    } else if (isConverted) {
      await MarketingCampaignEntry.restart(entry);
    } else {
      await MarketingCampaignEntry.resume(entry);
    }

    const logMessage = isRestart ? 'Entry re-enrolled' : 'Entry resumed';
    const performedBy = await this._getPerformedBy();
    await MarketingCampaignEntryLog.createEntryLog(
      entry,
      MarketingCampaignEntryLogEvent.StatusChange,
      {
        message: logMessage,
        performedBy,
      }
    );
  }

  private async _getPerformedBy(): Promise<
    INamedDocument<IStaffer> | undefined
  > {
    const staffer = await snapshot(
      this._organisation.staffer$.pipe(filterUndefined())
    );
    return stafferToNamedDoc(staffer);
  }

  private _getResumeLabel(entry: WithRef<IMarketingCampaignEntry>): string {
    if (
      entry.status === MarketingCampaignEntryStatus.Unenrolled ||
      entry.status === MarketingCampaignEntryStatus.Converted
    ) {
      return 'Re-enrol';
    }
    return 'Resume';
  }
}
